import { unsafeCSS } from 'lit'
import { customElement } from 'lit/decorators.js'

import { DdsElement } from '@/internal/dds-hu-element'
import { LabelableMixin } from '@/internal/mixin/labelableMixin'

import styles from './_styles/label.scss?inline'

/**
 * `dap-ds-label`
 * @summary A label is a container for labels intended for general use. It contains a label text, description, and tooltip.
 * @element dap-ds-label
 * @title - Label
 *
 * @property {string} label - The label text
 * @property {string} description - The description of the label
 * @property {string} tooltip - The tooltip text
 * @property {'xs' | 'sm' | 'lg'} size - The size of the input. Default is `sm`.
 * @property {'top' | 'right' | 'bottom' | 'left'} tooltipPlacement - The tooltip placement of the label.
 * @property {boolean} optional - If the label is optional. Default value is `false`.
 * @property {string} optionalLabel - Label of optional text
 * @property {boolean} subtle - Text weight of label. If true the label is subtle. Default value is `false`.
 * @property {boolean} disabled - The disabled state of the label. Default is `false`.
 * @property {boolean} required - The required state of the label. Default is `false`.
 *
 * @csspart base - The main label container.
 * @csspart label - Tha main label container. dap-ds-form-label element.
 * @csspart label-base - The label text.
 * @csspart label-required - The required indicator of the label.
 * @csspart label-optional - The optional indicator of the label.
 * @csspart tooltip - The tooltip of the label. dap-ds-tooltip element.
 * @csspart description - The description of the label.
 *
 * @cssproperty {string} --dds-label-background - The background color of the label container. Default is `var(--dds-background-neutral-subtle)`.
 * @cssproperty {string} --dds-label-border-color - The border color of the label container. Default is `var(--dds-border-neutral-subtle)`.
 * @cssproperty {string} --dds-label-border-width - The border width of the label container. Default is `var(--dds-border-width-base)`.
 * @cssproperty {string} --dds-label-border-radius - The border radius of the label container. Default is `var(--dds-radius-base)`.
 * @cssproperty {string} --dds-label-padding - The padding of the label container. Default is `var(--dds-spacing-400)`.
 * @cssproperty {string} --dds-label-disabled-background - The background color of the label container when disabled. Default is `var(--dds-background-neutral-disabled-inverted)`.
 * @cssproperty {string} --dds-label-checked-border-color - The border color of the label container when checked. Default is `var(--dds-background-brand-base-inverted)`.
 */
@customElement('dap-ds-label')
export default class DapDSLabel extends LabelableMixin(DdsElement) {
  static readonly styles = unsafeCSS(styles)

  render() {
    return this.renderLabel()
  }
}
